/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { AppConfigService, LanguageItem, LanguageService, UserPreferencesService } from '@alfresco/adf-core';

/**
 * The one place the interface language is read and changed.
 *
 * There were three: the sign-in screen, the settings page and the user menu,
 * each writing what it knew about. ADF keeps the preference under a key that
 * carries the signed-in user's name, so a language chosen from the user menu
 * (which goes through ADF's own menu) was invisible to the sign-in screen,
 * where nobody is signed in and the key is the guest one — the two screens
 * disagreed. Every change now goes through here, which writes both: ADF's for
 * the session, and the guest copy so the sign-in screen matches.
 */
@Injectable({ providedIn: 'root' })
export class AppLanguageService {
  private readonly config = inject(AppConfigService);
  private readonly preferences = inject(UserPreferencesService);
  private readonly language = inject(LanguageService);

  /** ADF reads preferences under `GUEST__*` until someone is signed in. */
  private static readonly GUEST_KEY = 'GUEST__locale';

  readonly languages: LanguageItem[] = this.config.get<LanguageItem[]>('languages', []) ?? [];

  readonly current = signal(this.preferences.locale ?? this.config.get<string>('locale', 'th'));

  labelFor(key: string): string {
    return this.languages.find((option) => option.key === key)?.label ?? key;
  }

  use(key: string): void {
    const item = this.languages.find((option) => option.key === key) ?? ({ key, label: key } as LanguageItem);

    // ADF's own path, so the text direction and <html lang> are set the way it
    // expects rather than by hand here
    this.language.changeLanguage(item);
    this.current.set(key);

    try {
      localStorage.setItem(AppLanguageService.GUEST_KEY, key);
    } catch {
      // a browser that refuses storage still gets the change for this session
    }
  }
}
